/*
 * <mobile-quick-add-form>
 *
 * Powers the modal rendered by snippets/mobile-quick-add.liquid. It resolves the
 * selected option values to a variant entirely client side — no section render
 * round trip — then syncs the hidden [name=id] input, the price, the submit
 * button state, the per-value availability styling, and the image gallery.
 *
 * Submitting is handled by <product-form> (assets/product-form.js); this element
 * only closes the modal once the item lands in the cart.
 */
if (!customElements.get('mobile-quick-add-form')) {
  customElements.define(
    'mobile-quick-add-form',
    class MobileQuickAddForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.variants = this.parseVariants();
        if (!this.variants.length) return;

        this.optionInputs = Array.from(this.querySelectorAll('[data-mqa-option]'));
        this.variantIdInput = this.querySelector('[data-mqa-variant-id]');
        this.submitButton = this.querySelector('[data-mqa-submit]');
        this.submitLabel = this.querySelector('[data-mqa-submit-label]');
        this.priceWrapper = this.querySelector('[data-mqa-price]');
        this.priceCurrent = this.querySelector('[data-mqa-price-current]');
        this.priceCompare = this.querySelector('[data-mqa-price-compare]');

        this.track = this.querySelector('[data-mqa-track]');
        this.slides = Array.from(this.querySelectorAll('[data-mqa-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-mqa-dot]'));

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-mqa-option]')) this.onOptionChange();
        });

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.scrollToSlide(Number(dot.dataset.mqaDot), true));
        });

        if (this.track && this.dots.length) {
          this.track.addEventListener('scroll', this.onTrackScroll.bind(this), { passive: true });
        }

        // Close the modal once <product-form> has added the variant to the cart.
        this.addEventListener('product-form:added', () => {
          this.closest('modal-dialog')?.hide();
        });

        this.updateFromSelection({ scrollGallery: false });
      }

      parseVariants() {
        const node = this.querySelector('[data-mqa-variants]');
        if (!node) return [];
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error('mobile-quick-add: could not parse variant data', error);
          return [];
        }
      }

      /* ---- Selection ---------------------------------------------------- */

      get selectedValues() {
        // Index i holds the value chosen for option position i + 1.
        return this.optionInputs
          .filter((input) => input.checked)
          .sort((a, b) => Number(a.dataset.mqaOption) - Number(b.dataset.mqaOption))
          .map((input) => input.value);
      }

      findVariant(values) {
        return this.variants.find(
          (variant) =>
            variant.options.length === values.length && variant.options.every((option, i) => option === values[i])
        );
      }

      onOptionChange() {
        this.updateFromSelection({ scrollGallery: true });
      }

      updateFromSelection({ scrollGallery }) {
        const values = this.selectedValues;
        // Products with only a default variant render no pickers, so fall back to
        // the id the form was rendered with.
        const variant = this.optionInputs.length
          ? this.findVariant(values)
          : this.variants.find((candidate) => String(candidate.id) === this.variantIdInput?.value);

        this.updateOptionAvailability(values);
        this.updateSelectedValueLabels();
        this.updatePrice(variant);
        this.updateSubmit(variant);

        if (variant && scrollGallery) this.scrollToVariantMedia(variant);
      }

      /*
       * A value is unavailable when no purchasable variant exists that pairs it with
       * every *other* currently selected value. Values are never disabled — the
       * shopper can still click through to a sold-out combination, matching how the
       * PDP picker behaves (allow_unavailable: true).
       */
      updateOptionAvailability(values) {
        this.optionInputs.forEach((input) => {
          const index = Number(input.dataset.mqaOption) - 1;
          const candidate = values.slice();
          candidate[index] = input.value;

          const reachable = this.variants.some(
            (variant) => variant.available && candidate.every((value, i) => variant.options[i] === value)
          );

          if (reachable) {
            input.removeAttribute('data-unavailable');
          } else {
            input.setAttribute('data-unavailable', 'true');
          }
          // visually-disabled is what reveals the .label-unavailable screen reader text
          // in component-product-variant-picker.css.
          input.classList.toggle('visually-disabled', !reachable);
          input.nextElementSibling?.classList.toggle('is-unavailable', !reachable);
        });
      }

      updateSelectedValueLabels() {
        this.querySelectorAll('[data-mqa-selected-value]').forEach((node) => {
          const position = node.dataset.mqaSelectedValue;
          const checked = this.optionInputs.find((input) => input.dataset.mqaOption === position && input.checked);
          if (checked) node.textContent = checked.value;
        });
      }

      updatePrice(variant) {
        if (!this.priceCurrent || !this.priceCompare || !this.priceWrapper) return;

        if (!variant) {
          this.priceCompare.hidden = true;
          this.priceWrapper.classList.remove('mqa__price--sale');
          return;
        }

        this.priceCurrent.textContent = variant.price_text;

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.priceCompare.hidden = !onSale;
        this.priceCompare.textContent = onSale ? variant.compare_at_price_text : '';
        this.priceWrapper.classList.toggle('mqa__price--sale', Boolean(onSale));
      }

      updateSubmit(variant) {
        if (!this.submitButton) return;

        const purchasable = Boolean(variant && variant.available);

        if (variant && this.variantIdInput) this.variantIdInput.value = variant.id;
        this.submitButton.disabled = !purchasable;
        this.submitButton.setAttribute('aria-disabled', String(!purchasable));

        if (!this.submitLabel) return;
        if (!variant) {
          this.submitLabel.textContent = window.variantStrings?.unavailable || 'Unavailable';
        } else if (variant.available) {
          this.submitLabel.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.submitLabel.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      /* ---- Gallery ------------------------------------------------------ */

      scrollToVariantMedia(variant) {
        if (!variant.featured_media_id || !this.slides.length) return;
        const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === variant.featured_media_id);
        if (index > -1) this.scrollToSlide(index, true);
      }

      scrollToSlide(index, smooth = false) {
        const slide = this.slides[index];
        if (!slide || !this.track) return;

        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        });
        this.setActiveDot(index);
      }

      onTrackScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;

          const center = this.track.scrollLeft + this.track.clientWidth / 2;
          let closest = 0;
          let closestDistance = Infinity;

          this.slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft + slide.offsetWidth / 2 - center);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = index;
            }
          });

          this.setActiveDot(closest);
        });
      }

      setActiveDot(index) {
        this.dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          if (active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
